import { DataTableRequest, Guid, HttpClient, IDataTableResponse, IDataTablesResponse, IDictionary, isNullOrEmpty } from "@ioip/core/lib";
import { ICreateChunkResponse } from "@ioip/core/lib/types/ICreateChunkResponse";
import { IConnectionSchema } from "../../connections";
import { IProcesoxEngineOverrideApi } from "../../core/IProcesoxEngineOverrideApi";
import { IEntitySchema } from "../../data";
import { IDataSourceSchema } from "../../datasources";
import { ICreateObjectRequest, IObject, IObjectEntry, IUpdateObjectRequest, ObjectType } from "../../portal/types";
import { CProject, IExtendedProjectSchema, IProjectSchema } from "../../projects";
import { IDataSourceResponse } from "../../types/IDataSourceResponse";
import { IJSPage } from "../../types/IJSPage";
import { IFormSchema } from "./IFormSchema";
import { JSPage } from "./JSPage";

export interface IJSEngineProps {
    httpClient: HttpClient;
    override?: IProcesoxEngineOverrideApi;
}

export class JSEngine {

    #props: IJSEngineProps;
    #pages: IDictionary<IJSPage> = {};
    #projectsV1: IDictionary<CProject> = {};

    constructor(props: IJSEngineProps) {
        this.#props = props;
    }

    async insertV1FormDataAsync(formVersionId: string, values: IDictionary<any>): Promise<string> {
        const apiUrl = `api/procesox/forms/${formVersionId}`;
        const result = await this.#props.httpClient.aadPost<string>(apiUrl, values);
        return result;
    }

    async updateV1FormDataAsync(formVersionId: string, itemId: any, values: IDictionary<any>): Promise<string> {
        const apiUrl = `api/procesox/forms/${formVersionId}/values?itemId=${itemId}`;
        const result = await this.#props.httpClient.aadPut<string>(apiUrl, values);
        return result;
    }

    async insertV2FormDataAsync(objectId: string, values: IDictionary<any>): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/pages/${objectId}`;
        const result = await this.#props.httpClient.aadPost<string>(apiUrl, values);
        return result;
    }

    async updateV2FormDataAsync(objectId: string, itemId: any, values: IDictionary<any>): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/pages/${objectId}/items/${itemId}`;
        const result = await this.#props.httpClient.aadPut<string>(apiUrl, values);
        return result;
    }

    async getPageV1Async(rootFormId: Guid, itemId: string | number = null): Promise<JSPage> {

        if (this.#pages[ rootFormId.toString() ] == null) {
            if (this.#props.override?.getPageV1Async != null) {
                const page = await this.#props.override.getPageV1Async(rootFormId, itemId);
                this.#pages[ rootFormId.toString() ] = page;
            } else {
                const apiUrl = `api/procesox/pages/${rootFormId}`;
                const page = await this.#props.httpClient.aadGetJson<IJSPage>(apiUrl);
                this.#pages[ rootFormId.toString() ] = page;
            }
        }

        // Si existen fuentes de datos nativas, se crea una instancia de Archivox
        const page = this.#pages[ rootFormId.toString() ];

        // Descargar datos de la página
        let values: IDictionary<any>;
        if (itemId != null && !isNullOrEmpty(String(itemId)))
            values = await this.getPageDataV1Async(rootFormId, itemId);

        const jsPage = new JSPage({
            schema: page,
            engine: this,
            values
        });

        return jsPage;

    }

    async getPageV2Async(rootFormId: Guid, itemId: string | number = null): Promise<JSPage> {

        if (this.#pages[ rootFormId.toString() ] == null) {
            if (this.#props.override?.getPageV2Async != null) {
                const page = await this.#props.override.getPageV2Async(rootFormId, itemId);
                this.#pages[ rootFormId.toString() ] = page;
            } else {
                const apiUrl = `api/procesox/v2.0.0/pages/${rootFormId}`;
                const page = await this.#props.httpClient.aadGetJson<IJSPage>(apiUrl);
                this.#pages[ rootFormId.toString() ] = page;
            }
        }

        // Si existen fuentes de datos nativas, se crea una instancia de Archivox
        const page = this.#pages[ rootFormId.toString() ];

        // Descargar datos de la página
        let values: IDictionary<any>;
        if (itemId != null && !isNullOrEmpty(String(itemId)))
            values = await this.getPageDataV1Async(rootFormId, itemId);

        const jsPage = new JSPage({
            schema: page,
            engine: this,
            values
        });

        return jsPage;

    }

    async getPageDataV1Async(formVersionId: Guid, itemId?: string | number): Promise<IDictionary<any>> {
        if (this.#props.override?.getPageDataV1Async != null) {
            const result = await this.#props.override.getPageDataV1Async(formVersionId, itemId);
            return result;
        } else {
            const apiUrl = `api/procesox/pages/${formVersionId}/data?itemId=${itemId}`;
            const result = await this.#props.httpClient.aadGetJson<IDictionary<any>>(apiUrl);
            return result;
        }
    }

    async getPageDataV2Async(objectId: Guid, itemId?: string | number): Promise<IDictionary<any>> {
        if (this.#props.override?.getPageDataV2Async != null) {
            const result = await this.#props.override.getPageDataV2Async(objectId, itemId);
            return result;
        } else {
            const apiUrl = `api/procesox/v2.0.0/pages/${objectId}/items/${itemId}`;
            const result = await this.#props.httpClient.aadGetJson<IDictionary<any>>(apiUrl);
            return result;
        }
    }

    async appendChunkContentAsync(formId: string, fieldId: string, documentId: string, buffer: Blob): Promise<ICreateChunkResponse> {
        return {
            Complete: false,
            DocumentId: 0,
            NextChunk: 0,
        }
    }

    async createChunkDocumentAsync(formId: string, fieldId: string, data: { length: number; name: string; fields: any; }): Promise<ICreateChunkResponse> {
        return {
            Complete: false,
            DocumentId: 0,
            NextChunk: 0,
        }
    }

    async downloadFieldDataSourceAsync(formVersionId: Guid, fieldId: Guid): Promise<IDataSourceResponse> {
        return {
            DataSourceType: 1,
            Items: [],
            KeyBinding: "",
            TextBinding: ""
        }
    }

    async downloadParametricEntityItems(entityId: string): Promise<IDictionary<any>[]> {
        return null as any;
    }

    async paginateEntityViewAsync(entityId: string, viewId: string, request: DataTableRequest): Promise<IDataTablesResponse<any>> {
        const apiUrl = `api/procesox/v2.0.0/entities/${entityId}/views/${viewId}/items?${request.toQeryString()}`;
        const result = await this.#props.httpClient.aadGetJson<IDataTablesResponse<any>>(apiUrl);
        return result;
    }

    /**
    * 
    * @param projectId 
    * @param request 
    * @returns 
    */
    async createDataSourceAsync(projectId: Guid, request: ICreateObjectRequest<IDataSourceSchema>): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/projects/${projectId}/datasources`;
        const result = await this.#props.httpClient.aadPost<string>(apiUrl, request);
        return result;
    }


    /**
     * Creates a new connection object in the specified project.
     * @param projectId The ID of the project in which to create the connection.
     * @param request The request object containing the connection schema.
     * @returns A Promise that resolves to the ID of the newly created connection.
     */
    async createConnectionAsync(projectId: Guid, request: ICreateObjectRequest<IConnectionSchema>): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/projects/${projectId}/connections`;
        const result = await this.#props.httpClient.aadPost<string>(apiUrl, request);
        return result;
    }


    /**
     * Creates a new entity in the specified project.
     * @param projectId The ID of the project where the entity will be created.
     * @param request The request object containing the entity schema.
     * @returns A Promise that resolves to the ID of the created entity.
     */
    async createEntityAsync(projectId: Guid, request: ICreateObjectRequest<IEntitySchema>): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/projects/${projectId}/entities`;
        const result = await this.#props.httpClient.aadPost<string>(apiUrl, request);
        return result;
    }

    /**
     * Obtiene un item de una entidad
     * @param entityId Identificador de la entidad
     * @param viewId Identificador de la vista
     * @param itemId Identificador del item
     * @returns 
     * @author jcorreal 2023-11-20
     */
    async getEntityItemAsync(entityId: string, viewId: string, itemId: number): Promise<IDictionary<any>> {
        const apiUrl = `api/procesox/v2.0.0/entities/${entityId}/views/${viewId}/items/${itemId}`;
        const result = await this.#props.httpClient.aadGetJson<IDictionary<any>>(apiUrl);
        return result;
    }

    /**
     * 
     * @param entityId 
     * @param viewId 
     * @param data 
     * @returns 
     * @author jcorreal 2023-11-20
     */
    async createEntityItemAsync(entityId: string, viewId: string, data: IDictionary<any>): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/entities/${entityId}/views/${viewId}/items`;
        const result = await this.#props.httpClient.aadPost<string>(apiUrl, data);
        return result;
    }

    /**
     * Actualiza un item de una entidad
     * @param entityId Identificador de la entidad
     * @param viewId Identificador de la vista
     * @param itemId Identificador del item
     * @param data Datos del item
     * @returns 
     * @author jcorreal 2023-11-20
     */
    async updateEntityItemAsync(entityId: string, viewId: string, itemId: number, data: IDictionary<any>): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/entities/${entityId}/views/${viewId}/items/${itemId}`;
        const result = await this.#props.httpClient.aadPut<string>(apiUrl, data);
        return result;
    }

    /**
     * Elimina un item de una entidad
     * @param entityId Identificador de la entidad
     * @param itemId Identificador del item
     * @author jcorreal 2023-11-20
     */
    async deleteEntityItemAsync(entityId: string, itemId: number): Promise<void> {
        const apiUrl = `api/procesox/v2.0.0/entities/${entityId}/items/${itemId}`;
        await this.#props.httpClient.aadDelete(apiUrl);
    }

    /**
     * Creates a new form in the specified project.
     * @param projectId The ID of the project to create the form in.
     * @param request The request object containing the form schema.
     * @returns A Promise that resolves to the ID of the newly created form.
     */
    async createFormsAsync(projectId: Guid, request: ICreateObjectRequest<IFormSchema>): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/projects/${projectId}/forms`;
        const result = await this.#props.httpClient.aadPost<string>(apiUrl, request);
        return result;
    }

    async updateFormAsync(objectId: Guid, request: IFormSchema): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/forms/${objectId}`;
        const result = await this.#props.httpClient.aadPut<string>(apiUrl, request);
        return result;
    }

    async updateEntityAsync(objectId: Guid, request: IObject<IEntitySchema>): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/entities/${objectId}`;
        const result = await this.#props.httpClient.aadPut<string>(apiUrl, request);
        return result;
    }

    /**
     * 
     * @param request 
     * @returns 
     */
    async createProjectAsync(request: IProjectSchema): Promise<string> {
        const apiUrl = `api/procesox/v2.0.0/projects`;
        const result = await this.#props.httpClient.aadPost<string>(apiUrl, request);
        return result;
    }

    /**
     * 
     * @param objectTypeId 
     * @param request 
     * @author jcorreal
     * @version 2023-10-22
     * @returns 
     */
    async paginateObjectAsync(objectTypeId: ObjectType, request: DataTableRequest): Promise<IDataTableResponse<IObjectEntry>> {
        const apiUrl = `api/procesox/v2.0.0/objects?objectType=${objectTypeId}&${request.toQeryString()}`;
        const result = await this.#props.httpClient.aadGetJson<IDataTableResponse<IObjectEntry>>(apiUrl);
        return result;
    }

    async paginateProjectsAsync(request: DataTableRequest): Promise<IDataTableResponse<IObjectEntry>> {
        const apiUrl = `api/procesox/v2.0.0/projects?${request.toQeryString()}`;
        const result = await this.#props.httpClient.aadGetJson<IDataTableResponse<IObjectEntry>>(apiUrl);
        return result;
    }

    async deleteObjectAsync(objectId: Guid): Promise<boolean> {
        return false;
    }

    async getDataSourceAsync(objectId: Guid): Promise<IObject<IDataSourceSchema>> {
        const apiUrl = `api/procesox/v2.0.0/datasources?objectId=${objectId}`;
        const result = await this.#props.httpClient.aadGetJson<IObject<IDataSourceSchema>>(apiUrl);
        return result;
    }

    /**
     * 
     * @param objectId 
     * @author jcorreal
     * @version 2023-10-22
     * @returns 
     */
    async getConnectionAsync(objectId: Guid): Promise<IObject<IConnectionSchema>> {
        const apiUrl = `api/procesox/v2.0.0/connections?objectId=${objectId}`;
        const result = await this.#props.httpClient.aadGetJson<IObject<IConnectionSchema>>(apiUrl);
        return result;
    }

    async getFormAsync(objectId: Guid): Promise<IObject<IFormSchema>> {
        const apiUrl = `api/procesox/v2.0.0/forms/${objectId}`;
        const result = await this.#props.httpClient.aadGetJson<IObject<IFormSchema>>(apiUrl);
        return result;
    }

    async getEntityAsync(objectId: Guid): Promise<IObject<IEntitySchema>> {
        const apiUrl = `api/procesox/v2.0.0/entities/${objectId}`;
        const result = await this.#props.httpClient.aadGetJson<IObject<IEntitySchema>>(apiUrl);
        return result;
    }

    /**
     * Retrieves the project schema for a given object ID.
     * @param objectId The ID of the project to retrieve the schema for.
     * @returns A Promise that resolves with the project schema.
     */
    async getProjectSchemaAsync(objectId: Guid): Promise<IProjectSchema> {
        const apiUrl = `api/procesox/v2.0.0/projects/${objectId}`;
        const result = await this.#props.httpClient.aadGetJson<IProjectSchema>(apiUrl);
        return result;
    }

    /**
     * Retrieves the extended schema for a project with the specified object ID.
     * @param objectId The object ID of the project to retrieve the extended schema for.
     * @returns A Promise that resolves with a Project instance representing the project with the retrieved extended schema.
     */
    async getExtendedProjectSchemaAsync(objectId: Guid): Promise<IExtendedProjectSchema> {
        const apiUrl = `api/procesox/v2.0.0/projects/${objectId}/extended`;
        const schema = await this.#props.httpClient.aadGetJson<IExtendedProjectSchema>(apiUrl);
        return schema;
    }

    async getProjectEntitiesAsync(projectId: Guid): Promise<IObject<IEntitySchema>[]> {
        const apiUrl = `api/procesox/v2.0.0/projects/${projectId}/entities/all`;
        const result = await this.#props.httpClient.aadGetJson<IObject<IEntitySchema>[]>(apiUrl);
        return result;
    }

    /**
     * 
     * @param objectId 
     * @param request 
     * @returns 
     */
    async executeConnectionAsync<T>(objectId: Guid, request: IDictionary<any>): Promise<T> {
        const apiUrl = `api/procesox/v2.0.0/connections/${objectId}/execute`;
        const result = await this.#props.httpClient.aadPost<T>(apiUrl, request);
        return result;
    }

    async executeEntityDataTableAsync<T>(objectId: string, fieldId: string, request: DataTableRequest): Promise<IDataTablesResponse<T>> {
        const apiUrl = `api/procesox/v2.0.0/forms/${objectId}/fields/${fieldId}/items?${request.toQeryString()}`;
        const result = await this.#props.httpClient.aadGetJson<IDataTablesResponse<T>>(apiUrl);
        return result;
    }

    async updateDataSourceAsync(objectId: Guid, request: IUpdateObjectRequest<IDataSourceSchema>): Promise<string> {
        return null;
    }

    async getRuntimeProjectV1Async(projectId: Guid, forceUpdate: boolean = false): Promise<CProject> {
        if (forceUpdate)
            delete this.#projectsV1[ projectId.toString() ];
        if (this.#projectsV1[ projectId.toString() ] == null) {
            const schema = await this.#props.httpClient.aadGetJson<IExtendedProjectSchema>(`api/procesox/v2/projects/${projectId}`);
            this.#projectsV1[ projectId.toString() ] = new CProject({ schema: schema });
        }
        return this.#projectsV1[ projectId.toString() ];
    }

}